import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const selections = new Map();
const cache = new Map();
const clone = (value) => JSON.parse(JSON.stringify(value));

export function quoteBundle(groups, count, campaign, currency, now = Date.now()) {
  if (!groups) return null;
  const base = groups.reduce((sum, line) => sum + line.variant.price * line.quantity, 0);
  let best = { base, saved: 0, total: base, discount: null };
  for (const discount of campaign.discounts ?? []) {
    const start = Date.parse(discount.starts_at);
    const end = discount.ends_at ? Date.parse(discount.ends_at) : Infinity;
    if (String(discount.product_id) !== String(campaign.product_id) || !['ACTIVE', 'SCHEDULED'].includes(discount.status) || !Number.isFinite(start) || start > now || end <= now || !Number.isInteger(discount.minimum_quantity) || discount.minimum_quantity > count) continue;
    const value = discount.value;
    let saved = 0;
    if (value?.type === 'percentage' && value.percentage > 0 && value.percentage <= 1) {
      saved = groups.reduce((sum, line) => sum + Math.floor(line.variant.price * value.percentage + 0.000001) * line.quantity, 0);
    } else if (value?.type === 'amount' && value.currency === currency && Number.isInteger(value.amount_minor) && value.amount_minor > 0) {
      saved = value.applies_on_each_item ? groups.reduce((sum, line) => sum + Math.min(line.variant.price, value.amount_minor) * line.quantity, 0) : Math.min(base, value.amount_minor);
    } else if (value?.type === 'buy_x_get_y' && Number.isInteger(value.buy_quantity) && value.buy_quantity > 0 && Number.isInteger(value.get_quantity) && value.get_quantity > 0 && value.percentage > 0 && value.percentage <= 1) {
      const units = groups.flatMap((line) => Array(line.quantity).fill(line.variant.price)).sort((a, b) => a - b);
      if (units.length !== count) continue;
      let uses = Math.floor(count / (value.buy_quantity + value.get_quantity));
      if (Number.isInteger(value.uses_per_order_limit)) uses = Math.min(uses, value.uses_per_order_limit);
      saved = units.slice(0, uses * value.get_quantity).reduce((sum, price) => sum + Math.floor(price * value.percentage + 0.000001), 0);
    }
    if (saved > best.saved || (saved > 0 && saved === best.saved && discount.minimum_quantity > (best.discount?.minimum_quantity ?? 0))) best = { base, saved, total: base - saved, discount };
  }
  // This display mode is enabled by the three product-owned campaigns only.
  // Shopify discounts still calculate from sale prices, never compare-at prices.
  if (campaign.show_compare_at_price === true && campaign.compare_at_price_mode === 'selected_variants') {
    const compareAtPrice = groups.reduce((sum, line) => {
      const original = Number.isInteger(line.variant.compareAtPrice) && line.variant.compareAtPrice > line.variant.price ? line.variant.compareAtPrice : line.variant.price;
      return sum + original * line.quantity;
    }, 0);
    return { ...best, compareAtPrice };
  }
  return best;
}

class AfzBundleSelector extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.config = JSON.parse(this.querySelector('[data-afz-bundle-config]').textContent);
    if (this.config.version !== 1 || this.config.enabled !== true || String(this.config.product_id) !== this.dataset.productId || this.config.source !== 'shopify_discount_nodes') { this.remove(); return; }
    this.quantities = [...this.querySelectorAll('[data-tier]')].map((tier) => Number(tier.dataset.tier)).filter((quantity) => Number.isInteger(quantity) && quantity > 0);
    if (!this.quantities.length) { this.remove(); return; }
    this.controller = new AbortController();
    this.strings = JSON.parse(this.querySelector('[data-afz-bundle-strings]').textContent);
    this.seed = JSON.parse(this.querySelector('[data-afz-bundle-state]').textContent);
    const saved = selections.get(this.dataset.productId);
    this.quantity = this.quantities.includes(saved?.quantity) ? saved.quantity : this.quantities.includes(this.config.default_quantity) ? this.config.default_quantity : this.quantities[0];
    this.picks = Array.from({ length: Math.max(...this.quantities) }, (_, index) => saved?.picks[index] ? clone(saved.picks[index]) : this.makePick(this.seed));
    this.currency = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency });
    this.section = this.closest('.shopify-section');
    this.section?.classList.add('afz-bundle-enabled');
    this.formComponent = this.section?.querySelector('product-form-component');
    this.form = this.formComponent?.querySelector('form[data-type="add-to-cart-form"]');
    // The theme delegates submit at document capture, before the form listener.
    // Let this product's bundle own submission to avoid also adding the first SKU.
    this.nativeSubmitHandler = this.formComponent?.getAttribute('on:submit');
    this.formComponent?.removeAttribute('on:submit');
    this.sticky = this.section?.querySelector('sticky-add-to-cart');
    const stickyButton = this.sticky?.querySelector('[ref="addToCartButton"]');
    this.nativeStickyClickHandler = stickyButton?.getAttribute('on:click');
    stickyButton?.removeAttribute('on:click');
    const signal = this.controller.signal;
    this.addEventListener('change', this.onChange, { signal });
    this.form?.addEventListener('submit', this.onSubmit, { capture: true, signal });
    this.sticky?.addEventListener('click', (event) => {
      if (event.target.closest('[ref="addToCartButton"]')) this.onSubmit(event);
    }, { capture: true, signal });
    this.section?.addEventListener(StandardEvents.productSelect, this.onNativeSelect, { signal });
    this.formComponent?.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.addEventListener('click', (event) => {
      if (this.canSubmit()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true, signal });
    this.setupSticky();
    this.render();
  }

  disconnectedCallback() {
    this.remember();
    this.controller?.abort();
    this.controller = null;
    this.buyButtonsObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.chatObserver?.disconnect();
    this.sticky?.querySelector('[ref="stickyBar"]')?.removeAttribute('data-afz-bundle-stuck');
    this.form?.querySelectorAll('[data-afz-bundle-input]').forEach((input) => input.remove());
    if (this.nativeSubmitHandler) this.formComponent?.setAttribute('on:submit', this.nativeSubmitHandler);
    if (this.nativeStickyClickHandler) this.sticky?.querySelector('[ref="addToCartButton"]')?.setAttribute('on:click', this.nativeStickyClickHandler);
    this.section?.classList.remove('afz-bundle-enabled', 'afz-bundle-multi');
  }

  makePick(state) {
    return { state: clone(state), ids: state.options.map((option) => String(option.values.find((value) => value.selected)?.id ?? option.values[0]?.id ?? '')), pending: false, error: '', generation: 0 };
  }

  money(cents) { return this.currency.format(cents / 100); }

  text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replaceAll('{{ ' + name + ' }}', String(value)), this.strings[key] || '');
  }

  remember() {
    if (!this.picks) return;
    selections.set(this.dataset.productId, { quantity: this.quantity, picks: this.picks.map((pick) => ({ ...clone(pick), pending: false })) });
  }

  grouped(count) {
    if (this.picks.length < count) return null;
    const grouped = new Map();
    for (const pick of this.picks.slice(0, count)) {
      const variant = pick.state.variant;
      if (!variant) return null;
      const previous = grouped.get(variant.id);
      if (previous) previous.quantity += 1;
      else grouped.set(variant.id, { variant, quantity: 1 });
    }
    return [...grouped.values()];
  }

  totals(count) {
    return quoteBundle(this.grouped(count), count, this.config, this.dataset.currency);
  }

  canSubmit() {
    if (this.busy || this.nativePending) return false;
    const picks = this.picks.slice(0, this.quantity);
    if (picks.some((pick) => pick.pending || pick.error || !pick.state.variant?.available)) return false;
    return this.grouped(this.quantity)?.every(({ variant, quantity }) =>
      quantity >= variant.minimum && quantity % variant.increment === 0 &&
      (variant.inventoryManaged !== 'shopify' || variant.inventoryPolicy !== 'deny' || quantity <= variant.inventoryQuantity)
    ) ?? false;
  }

  updateControls() {
    const ready = this.canSubmit();
    this.querySelectorAll('input, select').forEach((control) => { control.disabled = Boolean(this.busy); });
    this.formComponent?.querySelectorAll('button[type="submit"]').forEach((button) => {
      button.disabled = !ready;
      button.setAttribute('aria-disabled', String(!ready));
      button.setAttribute('aria-busy', String(Boolean(this.busy)));
    });
    this.section?.classList.toggle('afz-bundle-multi', this.quantity > 1);
    this.syncAcceleratedCheckout(ready);
    this.updateSticky(ready);
    const status = this.querySelector('[data-status]');
    if (this.busy) status.textContent = this.strings.adding;
    else if (this.nativePending || this.picks.slice(0, this.quantity).some((pick) => pick.pending)) status.textContent = this.strings.loading;
    else if (!ready && this.picks.slice(0, this.quantity).every((pick) => pick.state.variant?.available && !pick.error)) status.textContent = this.strings.stock_error;
    else if (status.textContent === this.strings.loading || status.textContent === this.strings.adding || status.textContent === this.strings.stock_error) status.textContent = '';
  }

  syncAcceleratedCheckout(ready) {
    if (!this.form) return;
    const groups = this.grouped(this.quantity);
    const variantInput = this.form.querySelector('input[name="id"]');
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    if (variantInput) {
      variantInput.value = String(groups?.[0]?.variant.id ?? '');
      variantInput.disabled = !ready;
    }
    if (quantityInput) quantityInput.value = String(groups?.[0]?.quantity ?? 1);
    this.form.querySelectorAll('[data-afz-bundle-input]').forEach((input) => input.remove());
    const additionalItems = document.createDocumentFragment();
    groups?.slice(1).forEach(({ variant, quantity }, index) => {
      for (const [attribute, value] of Object.entries({ id: variant.id, quantity })) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'shopify-additional-items[' + (index + 1) + '][' + attribute + ']';
        input.value = String(value);
        input.dataset.afzBundleInput = '';
        input.disabled = !ready;
        additionalItems.append(input);
      }
    });
    this.form.append(additionalItems);
    const paymentContainer = this.formComponent?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (paymentContainer) {
      // Multi-item buy/get offers need a cart before applying their native code.
      const needsCart = this.quantity > 1 && this.config.discounts.some((rule) => rule.value?.type === 'buy_x_get_y');
      paymentContainer.hidden = needsCart;
      paymentContainer.inert = !ready || needsCart;
      paymentContainer.setAttribute('aria-disabled', String(!ready || needsCart));
    }
  }

  setupSticky() {
    if (!this.sticky) return;
    this.buyButtonsBlock = this.formComponent?.closest('.buy-buttons-block');
    if (this.buyButtonsBlock) {
      this.buyButtonsObserver = new IntersectionObserver(() => this.updateStickyVisibility());
      this.buyButtonsObserver.observe(this.buyButtonsBlock);
    }
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        this.updateStickyVisibility();
      }, { rootMargin: '200px 0px 0px 0px' });
      this.footerObserver.observe(footer);
    }
    this.chatPanel = document.querySelector('dialog.chat-drawer');
    if (this.chatPanel) {
      this.chatObserver = new MutationObserver(() => this.updateStickyVisibility());
      this.chatObserver.observe(this.chatPanel, { attributes: true, attributeFilter: ['open'] });
    }
  }

  updateSticky(ready) {
    if (!this.sticky) return;
    const variants = this.picks.slice(0, this.quantity).map((pick) => pick.state.variant);
    this.sticky.dataset.variantAvailable = String(variants.every((variant) => variant?.available));
    const button = this.sticky.querySelector('[ref="addToCartButton"]');
    if (button) {
      button.removeAttribute('on:click');
      button.disabled = !ready;
      button.setAttribute('aria-disabled', String(!ready));
      button.setAttribute('aria-busy', String(Boolean(this.busy)));
    }
    const summary = this.sticky.querySelector('.sticky-add-to-cart__variant');
    if (summary) summary.textContent = this.quantity > 1 ? this.text('bundle_summary', { count: this.quantity }) : variants[0]?.options.join(' / ') ?? '';
    const quantityNumber = this.sticky.querySelector('[ref="quantityNumber"]');
    const quantityDisplay = this.sticky.querySelector('[ref="quantityDisplay"]');
    if (quantityNumber) quantityNumber.textContent = String(this.quantity);
    if (quantityDisplay) quantityDisplay.style.display = this.quantity > 1 ? 'inline' : 'none';
    const price = this.sticky.querySelector('.sticky-add-to-cart__price');
    const totals = this.totals(this.quantity);
    if (price) {
      const total = document.createElement('span');
      total.className = 'price';
      total.textContent = totals ? this.money(totals.total) : '—';
      price.replaceChildren(total);
      if (totals && (totals.compareAtPrice ?? totals.base) > totals.total) {
        const base = document.createElement('s');
        base.className = 'compare-at-price';
        base.textContent = this.money(totals.compareAtPrice ?? totals.base);
        price.append(base);
      }
    }
    this.updateStickyVisibility();
  }

  updateStickyVisibility() {
    const bar = this.sticky?.querySelector('[ref="stickyBar"]');
    if (!bar || !this.buyButtonsBlock) return;
    const scrolledPast = this.buyButtonsBlock.getBoundingClientRect().bottom < 0;
    const visible = scrolledPast && !this.footerVisible && !this.chatPanel?.open && this.sticky.dataset.variantAvailable === 'true';
    bar.dataset.afzBundleStuck = String(visible);
    bar.inert = !visible;
    bar.setAttribute('aria-hidden', String(!visible));
  }

  render() {
    const focused = document.activeElement?.closest('afz-bundle-selector') === this ? document.activeElement : null;
    const focusIndex = focused?.closest('[data-item-index]')?.dataset.itemIndex;
    const focusOption = focused?.dataset.optionIndex;
    for (const tier of this.querySelectorAll('[data-tier]')) {
      const count = Number(tier.dataset.tier);
      const selected = count === this.quantity;
      tier.classList.toggle('is-selected', selected);
      tier.querySelector('input[type="radio"]').checked = selected;
      tier.querySelector('[data-tier-content]').hidden = !selected || count === 1;
      const totals = this.totals(count);
      tier.querySelector('[data-tier-total]').textContent = totals ? this.money(totals.total) : '—';
      tier.querySelector('[data-tier-base]').textContent = totals ? this.money(totals.compareAtPrice ?? totals.base) : '';
      tier.querySelector('[data-tier-base]').hidden = !totals || (totals.compareAtPrice ?? totals.base) <= totals.total;
      const value = totals?.discount?.value;
      const tierConfig = this.config.tiers.find((item) => item.quantity === count);
      tier.querySelector('[data-tier-benefit]').textContent = value?.type === 'buy_x_get_y' ? (tierConfig?.description || 'Buy ' + value.buy_quantity + ', Get ' + value.get_quantity + ' Free') : value?.type === 'percentage' ? this.text('percent_off', { percent: Math.round(value.percentage * 10000) / 100 }) : value?.type === 'amount' ? this.text('amount_off', { amount: this.money(totals.saved) }) : (tierConfig?.description || this.strings.single);
      const savingKey = totals?.compareAtPrice !== undefined && this.strings.save_original ? 'save_original' : 'save';
      const savedAmount = totals?.compareAtPrice !== undefined ? totals.compareAtPrice - totals.total : totals?.saved;
      tier.querySelector('[data-tier-saving]').textContent = count > 1 && totals ? this.text(savingKey, { amount: this.money(savedAmount), each: this.money(Math.round(totals.total / count)) }) : '';
      if (!selected || count === 1) continue;
      const rows = tier.querySelector('[data-item-rows]');
      rows.replaceChildren();
      this.picks.slice(0, count).forEach((pick, index) => rows.append(this.createRow(pick, index)));
    }
    this.remember();
    this.updateControls();
    if (focusIndex !== undefined && focusOption !== undefined) {
      this.querySelector('.is-selected [data-item-index="' + focusIndex + '"] select[data-option-index="' + focusOption + '"]')?.focus({ preventScroll: true });
    }
  }

  createRow(pick, index) {
    const row = document.createElement('div');
    row.className = 'afz-bundle__row';
    row.dataset.itemIndex = String(index);
    row.dataset.variantId = String(pick.state.variant?.id ?? '');
    row.setAttribute('aria-busy', String(pick.pending));
    const marker = document.createElement('div');
    marker.className = 'afz-bundle__item';
    const number = document.createElement('span');
    number.textContent = '#' + (index + 1);
    marker.append(number);
    if (pick.state.image) {
      const image = document.createElement('img');
      image.className = 'afz-bundle__image';
      image.src = pick.state.image;
      image.alt = '';
      image.width = 36;
      image.height = 44;
      image.loading = 'lazy';
      marker.append(image);
    }
    row.append(marker);
    const fields = document.createElement('div');
    fields.className = 'afz-bundle__fields';
    fields.style.setProperty('--bundle-options', pick.state.options.length);
    pick.state.options.forEach((option, position) => {
      const label = document.createElement('label');
      label.className = 'afz-bundle__field';
      const name = document.createElement('span');
      name.textContent = option.name;
      const select = document.createElement('select');
      select.dataset.optionIndex = String(position);
      select.setAttribute('aria-label', this.text('item', { number: index + 1 }) + ' · ' + option.name);
      for (const value of option.values) {
        const node = document.createElement('option');
        node.value = String(value.id);
        node.textContent = value.name;
        select.append(node);
      }
      select.value = pick.ids[position];
      label.append(name, select);
      fields.append(label);
    });
    row.append(fields);
    const price = document.createElement('span');
    price.className = 'afz-bundle__row-price';
    price.textContent = pick.state.variant ? this.text('each', { amount: this.money(pick.state.variant.price) }) : '';
    row.append(price);
    const error = document.createElement('p');
    error.className = 'afz-bundle__row-error';
    error.textContent = pick.pending ? this.strings.loading : pick.error || (!pick.state.variant ? this.strings.unavailable : !pick.state.variant.available ? this.strings.sold_out : '');
    error.hidden = !error.textContent;
    row.append(error);
    return row;
  }

  onChange = (event) => {
    if (event.target.matches('input[type="radio"]')) {
      const quantity = Number(event.target.value);
      if (!this.quantities.includes(quantity)) return;
      this.quantity = quantity;
      if (this.quantity === 1) this.picks[0] = this.makePick(this.seed);
      this.querySelector('[data-status]').textContent = '';
      this.render();
    } else if (event.target.matches('select[data-option-index]')) {
      const index = Number(event.target.closest('[data-item-index]').dataset.itemIndex);
      const pick = this.picks[index];
      pick.ids[Number(event.target.dataset.optionIndex)] = event.target.value;
      this.resolve(index);
    }
  };

  async resolve(index) {
    const pick = this.picks[index];
    const generation = ++pick.generation;
    const key = this.dataset.productId + ':' + pick.ids.join(',');
    pick.pending = true;
    pick.error = '';
    this.updateControls();
    const row = this.querySelector('.is-selected [data-item-index="' + index + '"]');
    if (row) {
      row.setAttribute('aria-busy', 'true');
      row.querySelector('.afz-bundle__row-error').hidden = false;
      row.querySelector('.afz-bundle__row-error').textContent = this.strings.loading;
    }
    try {
      let state = cache.get(key);
      if (!state) {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', 'afz-bundle-variant');
        url.searchParams.set('option_values', pick.ids.join(','));
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Variant resolution failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const stateNode = html.querySelector('[data-afz-bundle-state]');
        if (!stateNode) throw new Error('Variant state missing');
        state = JSON.parse(stateNode.textContent);
        if (String(state.productId) !== this.dataset.productId) throw new Error('Product mismatch');
        cache.set(key, state);
      }
      if (generation !== pick.generation || !this.isConnected) return;
      pick.state = clone(state);
    } catch (error) {
      if (error.name === 'AbortError' || generation !== pick.generation) return;
      pick.error = this.strings.retry;
    } finally {
      if (generation === pick.generation && this.isConnected) {
        pick.pending = false;
        this.render();
      }
    }
  }

  onNativeSelect = async (event) => {
    if (String(event.product?.id ?? this.dataset.productId).split('/').at(-1) !== this.dataset.productId) return;
    const generation = (this.nativeGeneration ?? 0) + 1;
    this.nativeGeneration = generation;
    this.nativePending = true;
    this.updateControls();
    try {
      const result = await event.promise;
      if (generation !== this.nativeGeneration || !this.isConnected) return;
      const node = result.detail?.html?.querySelector('[data-afz-bundle-state]');
      if (node) {
        const state = JSON.parse(node.textContent);
        if (String(state.productId) === this.dataset.productId) {
          this.seed = state;
          this.picks[0] = this.makePick(state);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError' && generation === this.nativeGeneration) this.picks[0].error = this.strings.retry;
    } finally {
      if (generation === this.nativeGeneration && this.isConnected) {
        this.nativePending = false;
        this.render();
      }
    }
  };

  onSubmit = async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.canSubmit()) return;
    this.busy = true;
    this.updateControls();
    const quantity = this.quantity;
    const lines = this.grouped(quantity);
    const bundleId = crypto.randomUUID();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.formComponent.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: lines.map(({ variant, quantity }) => ({ merchandiseId: String(variant.id), quantity })),
      promise: deferred.promise
    }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean);
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map(({ variant, quantity: itemQuantity }) => ({ id: variant.id, quantity: itemQuantity, properties: { _afz_bundle_id: bundleId, _afz_bundle_quantity: String(quantity) } })),
          sections: [...new Set(sectionIds)].join(','),
          sections_url: window.location.pathname
        })
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || this.strings.cart_error);
      // Wait for native Shopify code application before announcing a completed add.
      const discountResult = this.config.discounts.some((rule) => rule.method === 'code') && window.AFZBundleDiscounts?.sync
        ? await window.AFZBundleDiscounts.sync({ resetProductId: this.dataset.productId })
        : null;
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.strings.cart_error);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'product-form-component', sourceId: this.formComponent.id || this.id, itemCount: quantity, productId: this.dataset.productId, sections: discountResult?.sections || added.sections, didError: false }
      });
      this.querySelector('[data-status]').textContent = this.strings.added;
    } catch (error) {
      deferred.reject(error);
      this.querySelector('[data-status]').textContent = error.message || this.strings.cart_error;
      this.formComponent.dispatchEvent(new CartErrorEvent({ error: error.message || this.strings.cart_error, code: 'INVALID' }));
    } finally {
      this.busy = false;
      this.updateControls();
    }
  };
}

if (!customElements.get('afz-bundle-selector')) customElements.define('afz-bundle-selector', AfzBundleSelector);

